import { useCallback, useEffect, useState } from "react";
import { api, API, fmtErr } from "../lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Skeleton } from "../components/ui/skeleton";
import { toast } from "sonner";
import { DownloadSimple } from "@phosphor-icons/react";
import {
  Bar, BarChart, CartesianGrid, Cell, Legend, Line, LineChart, Pie, PieChart,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { useTheme } from "../context/ThemeContext";
import { tc } from "../lib/theme";

const C = { blue: "var(--c-accent)", green: "var(--c-green)", amber: "var(--c-amber)", red: "var(--c-red)", gray: "var(--c-muted)", cyan: "#0891b2" };
const STAGE_COLORS = [C.blue, C.green, C.amber, C.cyan, "#7c3aed", "#db2777", "#65a30d", "#ea580c", "#4f46e5", "#0d9488", C.red];

const RANGES = [["all", "All time"], ["7", "Last 7 days"], ["30", "Last 30 days"], ["90", "Last 90 days"]];

const tipStyle = { contentStyle: { background: "var(--c-surface)", border: "1px solid var(--c-border)", fontSize: 12 }, labelStyle: { color: "var(--c-text)" } };

function ChartSkeleton({ h = 220 }) {
  return <Skeleton className="w-full bg-[var(--c-border)]/50 animate-pulse rounded-sm" style={{ height: h }} data-testid="chart-skeleton" />;
}

function Card({ title, hint, children, testid, className = "" }) {
  return (
    <div className={`bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm p-4 ${className}`} data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <h3 className="text-xs font-mono uppercase tracking-widest text-[var(--c-muted)]">{title}</h3>
        {hint && <span className="text-[10px] text-[var(--c-muted)]/70" title={hint}>ⓘ</span>}
      </div>
      {children}
    </div>
  );
}

function Stat({ label, value, suffix = "", color = "var(--c-text)", testid }) {
  return (
    <div className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm p-4" data-testid={testid}>
      <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--c-muted)]">{label}</div>
      <div className="text-2xl font-semibold mt-1" style={{ color }}>
        {value == null ? "—" : value}{value != null && suffix}
      </div>
    </div>
  );
}

function Gauge({ pct }) {
  const v = Math.max(0, Math.min(100, pct ?? 0));
  const color = pct == null ? C.gray : v >= 90 ? C.green : v >= 70 ? C.amber : C.red;
  const r = 52, circ = Math.PI * r;
  return (
    <div className="flex items-center justify-center" data-testid="ontime-gauge">
      <svg width="140" height="90" viewBox="0 0 140 90">
        <path d={`M 16 80 A ${r} ${r} 0 0 1 124 80`} fill="none" style={{ stroke: "var(--c-border)" }} strokeWidth="10" strokeLinecap="round" />
        <path d={`M 16 80 A ${r} ${r} 0 0 1 124 80`} fill="none" style={{ stroke: color }} strokeWidth="10" strokeLinecap="round"
          strokeDasharray={`${(v / 100) * circ} ${circ}`} />
        <text x="70" y="72" textAnchor="middle" style={{ fill: color }} fontSize="22" fontWeight="700">{pct == null ? "—" : `${v}%`}</text>
      </svg>
    </div>
  );
}

function Gantt({ rows }) {
  const times = [];
  rows.forEach((r) => r.segments.forEach((s) => {
    if (s.start) times.push(new Date(s.start).getTime());
    times.push(s.end ? new Date(s.end).getTime() : Date.now());
  }));
  if (!times.length) return <div className="text-xs text-[var(--c-muted)]">No stage history yet</div>;
  const min = Math.min(...times), max = Math.max(...times, min + 86400000);
  const span = max - min;
  const stageIdx = {};
  let ni = 0;
  rows.forEach((r) => r.segments.forEach((s) => { if (!(s.stage in stageIdx)) stageIdx[s.stage] = ni++ % STAGE_COLORS.length; }));
  return (
    <div className="space-y-1.5" data-testid="gantt-view">
      {rows.map((r) => (
        <div key={r.id} className="flex items-center gap-3" data-testid={`gantt-row-${r.code}`}>
          <a href={`/projects/${r.id}`} className="w-40 shrink-0 text-[11px] font-mono text-[var(--c-muted)] hover:text-[var(--c-accent)] truncate" title={r.summary}>{r.code}</a>
          <div className="relative flex-1 h-4 bg-[var(--c-bg)] rounded-sm overflow-hidden">
            {r.segments.map((s, i) => {
              const st = s.start ? new Date(s.start).getTime() : min;
              const en = s.end ? new Date(s.end).getTime() : Date.now();
              const left = ((st - min) / span) * 100;
              const width = Math.max(((en - st) / span) * 100, 1.2);
              return <div key={i} className="absolute top-0 h-full rounded-sm" title={`${s.name}: ${new Date(st).toLocaleDateString()} → ${s.end ? new Date(en).toLocaleDateString() : "now"}`}
                style={{ left: `${left}%`, width: `${width}%`, background: STAGE_COLORS[stageIdx[s.stage]], opacity: s.end ? 0.55 : 1 }} />;
            })}
          </div>
        </div>
      ))}
      <div className="flex flex-wrap gap-3 pt-2">
        {Object.entries(stageIdx).map(([k, i]) => (
          <span key={k} className="flex items-center gap-1 text-[10px] text-[var(--c-muted)]">
            <span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ background: STAGE_COLORS[i] }} />{k}
          </span>
        ))}
      </div>
    </div>
  );
}

export default function Analytics() {
  useTheme();
  const RC = { blue: tc("accent"), green: tc("green"), amber: tc("amber"), red: tc("red"), gray: tc("muted"), cyan: "#0891b2", border: tc("border"), muted: tc("muted") };
  const axisProps = { tick: { fill: RC.muted, fontSize: 10 }, axisLine: { stroke: RC.border }, tickLine: false };
  const AE = { a: RC.blue, b: RC.red, c: RC.amber, d: RC.cyan, e: RC.green };
  const PIE_R = [RC.blue, RC.green, RC.amber, RC.red, RC.cyan, RC.gray];
  const [range, setRange] = useState("all");
  const [tab, setTab] = useState("operations");
  const [data, setData] = useState({});
  const [loading, setLoading] = useState({});
  const [drill, setDrill] = useState(null);

  const params = range === "all" ? "" : `?frm=${new Date(Date.now() - Number(range) * 86400000).toISOString()}`;

  const load = useCallback(async (t) => {
    setLoading((l) => ({ ...l, [t]: true }));
    try {
      const { data: d } = await api.get(`/analytics/${t}${params}`);
      setData((prev) => ({ ...prev, [t]: d }));
    } catch (e) { toast.error(fmtErr(e)); } finally { setLoading((l) => ({ ...l, [t]: false })); }
  }, [range]);

  useEffect(() => { load(tab); }, [tab, range]);

  const ops = data.operations, qual = data.quality, eff = data.efficiency, ind = data.industry;

  return (
    <div className="p-6 space-y-5" data-testid="analytics-page">
      <div className="flex items-center gap-3 flex-wrap">
        <div>
          <h2 className="text-base font-medium text-[var(--c-text)]">Analytics</h2>
          <p className="text-xs text-[var(--c-muted)] mt-0.5">Operations · Quality · Efficiency · Industry comparison</p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Select value={range} onValueChange={setRange}>
            <SelectTrigger data-testid="analytics-range" className="w-40 h-8 bg-[var(--c-surface)] border-[var(--c-border)] text-xs text-[var(--c-text)]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[var(--c-surface)] border-[var(--c-border)]">
              {RANGES.map(([v, l]) => <SelectItem key={v} value={v} className="text-xs text-[var(--c-text)] focus:bg-[var(--c-surface-2)]">{l}</SelectItem>)}
            </SelectContent>
          </Select>
          <a href={`${API}/analytics/export.pdf${params}`} data-testid="analytics-export-pdf"
            className="h-8 px-3 flex items-center gap-1.5 text-[11px] rounded-sm border border-[var(--c-border)] text-[var(--c-muted)] hover:text-[var(--c-text)] hover:border-[var(--c-accent)] transition-colors">
            <DownloadSimple size={13} /> Export PDF
          </a>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-[var(--c-surface)] border border-[var(--c-border)]" data-testid="analytics-tabs">
          {[["operations", "Operations"], ["quality", "Quality"], ["efficiency", "Efficiency"], ["industry", "Industry Comparison"]].map(([v, l]) => (
            <TabsTrigger key={v} value={v} data-testid={`analytics-tab-${v}`}
              className="text-xs data-[state=active]:bg-[var(--c-accent)]/15 data-[state=active]:text-[var(--c-text)] text-[var(--c-muted)]">{l}</TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="operations" className="mt-4 space-y-4">
          {loading.operations || !ops ? <div className="grid grid-cols-2 gap-4"><ChartSkeleton /><ChartSkeleton /><ChartSkeleton /><ChartSkeleton /></div> : (
            <>
              <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
                <Stat label="Total projects" value={ops.total_projects} testid="ops-total" />
                <Stat label="Active" value={ops.active} color={C.blue} testid="ops-active" />
                <Stat label="Overdue" value={ops.overdue} color={ops.overdue ? C.red : C.green} testid="ops-overdue" />
                <Card title="On-time rate" hint="% of completed stage segments finished within their SLA" testid="ops-ontime">
                  <Gauge pct={ops.on_time_rate} />
                </Card>
              </div>
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                <Card title="Projects by stage" testid="ops-funnel">
                  <div className="space-y-1.5">
                    {ops.funnel.map((f, i) => {
                      const mx = Math.max(...ops.funnel.map((x) => x.count), 1);
                      return (
                        <div key={f.stage} className="flex items-center gap-2" data-testid={`funnel-${f.stage}`}>
                          <span className="w-28 text-[11px] text-[var(--c-muted)] truncate">{f.name}</span>
                          <div className="flex-1 h-5 bg-[var(--c-bg)] rounded-sm overflow-hidden">
                            <div className="h-full rounded-sm transition-all duration-500"
                              style={{ width: `${(f.count / mx) * 100}%`, background: STAGE_COLORS[i % STAGE_COLORS.length] }} />
                          </div>
                          <span className="w-8 text-right text-[11px] font-mono text-[var(--c-text)]">{f.count}</span>
                        </div>
                      );
                    })}
                  </div>
                </Card>
                <Card title="Avg cycle time per stage (days)" hint="Click a bar to drill down into the slowest projects" testid="ops-cycle">
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={ops.cycle_time.filter((c) => c.samples > 0 || c.avg_days > 0)}>
                      <CartesianGrid stroke={RC.border} strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="name" {...axisProps} interval={0} angle={-25} textAnchor="end" height={50} />
                      <YAxis {...axisProps} />
                      <Tooltip {...tipStyle} formatter={(v, _n, p) => [`${v} d (${p.payload.samples} samples)`, "avg"]} />
                      <Bar dataKey="avg_days" fill={RC.blue} radius={[2, 2, 0, 0]} cursor="pointer"
                        onClick={(d) => setDrill(d?.activePayload?.[0]?.payload || null)} />
                    </BarChart>
                  </ResponsiveContainer>
                  {drill && (
                    <div className="mt-2 border-t border-[var(--c-border)] pt-2" data-testid="cycle-drilldown">
                      <div className="text-[10px] font-mono uppercase text-[var(--c-muted)] mb-1">Slowest in {drill.name} <button className="text-[var(--c-accent)] ml-2" onClick={() => setDrill(null)}>clear</button></div>
                      {(drill.projects || []).map((p) => (
                        <a key={p.id} href={`/projects/${p.id}`} className="flex justify-between text-[11px] py-0.5 text-[var(--c-muted)] hover:text-[var(--c-text)]">
                          <span className="font-mono">{p.code}</span><span>{p.days}d</span>
                        </a>
                      ))}
                    </div>
                  )}
                </Card>
                <Card title="Active projects per engineer" testid="ops-engineers">
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={ops.per_engineer} layout="vertical">
                      <CartesianGrid stroke={RC.border} strokeDasharray="3 3" horizontal={false} />
                      <XAxis type="number" {...axisProps} allowDecimals={false} />
                      <YAxis type="category" dataKey="engineer" width={110} {...axisProps} />
                      <Tooltip {...tipStyle} />
                      <Bar dataKey="count" fill={RC.green} radius={[0, 2, 2, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
                <Card title="Recent projects — Gantt" testid="ops-gantt"><Gantt rows={ops.gantt} /></Card>
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="quality" className="mt-4 space-y-4">
          {loading.quality || !qual ? <div className="grid grid-cols-2 gap-4"><ChartSkeleton /><ChartSkeleton /></div> : (
            <>
              <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
                <Stat label="Avg revisions / project" value={qual.avg_revisions} testid="q-revisions" />
                <Stat label="First-pass validation" value={qual.first_pass_validation_pct} suffix="%" color={(qual.first_pass_validation_pct ?? 0) >= 80 ? C.green : C.amber} testid="q-first-pass" />
                <Stat label="Client approval first-pass" value={qual.client_first_pass_pct} suffix="%" color={(qual.client_first_pass_pct ?? 0) >= 80 ? C.green : C.amber} testid="q-client-fp" />
                <Stat label="Design reuse" value={qual.design_reuse_pct} suffix="%" color={C.blue} testid="q-reuse" />
              </div>
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                <Card title="Avg revisions per project — trend" testid="q-trend">
                  {qual.revisions_trend.length ? (
                    <ResponsiveContainer width="100%" height={220}>
                      <LineChart data={qual.revisions_trend}>
                        <CartesianGrid stroke={RC.border} strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="month" {...axisProps} /><YAxis {...axisProps} />
                        <Tooltip {...tipStyle} />
                        <Line type="monotone" dataKey="avg_revisions" stroke={RC.blue} strokeWidth={2} dot={{ fill: RC.blue }} />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : <div className="text-xs text-[var(--c-muted)]">No revision data in range</div>}
                </Card>
                <Card title="Revision sources (a–e) per month" hint="a client queries · b validation errors · c reviewer errors · d cross-functional · e client drawing revisions" testid="q-sources">
                  {qual.revision_sources.length ? (
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={qual.revision_sources}>
                        <CartesianGrid stroke={RC.border} strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="month" {...axisProps} /><YAxis {...axisProps} allowDecimals={false} />
                        <Tooltip {...tipStyle} /><Legend wrapperStyle={{ fontSize: 10 }} />
                        {qual.source_names.map((s) => (
                          <Bar key={s.key} dataKey={s.key} name={`${s.key}: ${s.name}`} stackId="src" fill={AE[s.key]} />
                        ))}
                      </BarChart>
                    </ResponsiveContainer>
                  ) : <div className="text-xs text-[var(--c-muted)]">No revision events in range</div>}
                </Card>
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="efficiency" className="mt-4 space-y-4">
          {loading.efficiency || !eff ? <div className="grid grid-cols-2 gap-4"><ChartSkeleton /><ChartSkeleton /></div> : (
            <>
              <Card title="Manual benchmark vs automated (hours per stage)" hint={eff.timing_note || "Actual automated processing time recorded per project"} testid="eff-chart">
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={eff.stages}>
                    <CartesianGrid stroke={RC.border} strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="stage_name" {...axisProps} interval={0} angle={-25} textAnchor="end" height={60} />
                    <YAxis {...axisProps} />
                    <Tooltip {...tipStyle} /><Legend wrapperStyle={{ fontSize: 10 }} />
                    <Bar dataKey="manual_hours" name="Manual benchmark" fill={RC.gray} radius={[2, 2, 0, 0]} />
                    <Bar dataKey={(r) => r.automated_hours ?? r.automated_target} name="Automated (actual/target)" fill={RC.green} radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
                <div className="mt-2 text-[11px] font-mono text-[var(--c-muted)]" data-testid="eff-total">
                  Total: manual {eff.total_manual_hours} hrs → automated {eff.total_automated_hours || eff.total_automated_target} hrs
                  {eff.total_manual_hours > 0 && (
                    <span className="text-[var(--c-green)]"> ({(100 - 100 * (eff.total_automated_hours || eff.total_automated_target) / eff.total_manual_hours).toFixed(1)}% reduction)</span>
                  )}
                  {eff.timing_note && <span className="block text-[10px] text-[var(--c-amber)] mt-1">{eff.timing_note}</span>}
                </div>
              </Card>
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                <Card title="AI Copilot usage" testid="eff-copilot">
                  <div className="grid grid-cols-2 gap-3 mb-2">
                    <Stat label="Prompts / project (avg)" value={eff.copilot.avg_prompts_per_project} testid="copilot-avg" />
                    <Stat label="Projects using Copilot" value={eff.copilot.projects_using} testid="copilot-projects" />
                  </div>
                  {eff.copilot.action_types.length ? (
                    <ResponsiveContainer width="100%" height={180}>
                      <PieChart>
                        <Pie data={eff.copilot.action_types} dataKey="count" nameKey="type" innerRadius={40} outerRadius={70}
                          label={(e) => e.type} labelLine={false} fontSize={10}>
                          {eff.copilot.action_types.map((_, i) => <Cell key={i} fill={PIE_R[i % PIE_R.length]} />)}
                        </Pie>
                        <Tooltip {...tipStyle} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : <div className="text-xs text-[var(--c-muted)]">No copilot actions yet</div>}
                </Card>
                <Card title="Drawing generation time (avg seconds)" testid="eff-drawing-times">
                  {eff.drawing_times.length ? (
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={eff.drawing_times} layout="vertical">
                        <CartesianGrid stroke={RC.border} strokeDasharray="3 3" horizontal={false} />
                        <XAxis type="number" {...axisProps} />
                        <YAxis type="category" dataKey="type" width={120} {...axisProps} />
                        <Tooltip {...tipStyle} />
                        <Bar dataKey="avg_seconds" fill={RC.cyan} radius={[0, 2, 2, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : <div className="text-xs text-[var(--c-muted)]">Drawing generation timing appears here as new drawings are generated</div>}
                </Card>
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="industry" className="mt-4">
          {loading.industry || !ind ? <ChartSkeleton h={300} /> : (
            <Card title="Industry comparison" testid="industry-table">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-[var(--c-border)] text-[10px] font-mono uppercase tracking-widest text-[var(--c-muted)]">
                    <th className="py-2">Industry</th><th>Projects</th><th>Avg cycle (days)</th>
                    <th>First-pass validation</th><th>Client first-pass</th><th>Avg revisions</th><th>Design reuse</th>
                  </tr>
                </thead>
                <tbody>
                  {ind.industries.map((r) => (
                    <tr key={r.industry} className="border-b border-[var(--c-border)]/50 text-[12px]" data-testid={`ind-row-${r.industry}`}>
                      <td className="py-2 text-[var(--c-text)]">{r.name}</td>
                      <td className="font-mono">{r.projects}</td>
                      <td className="font-mono">{r.avg_cycle_days}</td>
                      <td className="font-mono" style={{ color: (r.first_pass_validation_pct ?? 0) >= 80 ? C.green : C.amber }}>
                        {r.first_pass_validation_pct == null ? "—" : `${r.first_pass_validation_pct}%`}
                        {r.first_pass_validation_pct != null && r.first_pass_validation_pct === Math.max(...ind.industries.map((x) => x.first_pass_validation_pct ?? -1)) && " ★"}
                      </td>
                      <td className="font-mono">{r.client_first_pass_pct == null ? "—" : `${r.client_first_pass_pct}%`}</td>
                      <td className="font-mono" style={{ color: r.avg_revisions <= Math.min(...ind.industries.map((x) => x.avg_revisions)) ? C.green : undefined }}>
                        {r.avg_revisions}
                        {r.avg_revisions === Math.min(...ind.industries.map((x) => x.avg_revisions)) && " ★"}
                      </td>
                      <td className="font-mono">{r.design_reuse_pct}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="text-[10px] text-[var(--c-muted)] mt-2">★ best in column — highest first-pass, lowest revisions</div>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
